import { createContext, useContext, useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMediaQuery } from "@/hooks/use-media-query";

const ThemeContext = createContext(null);
const KEY = "cashmy.theme";
const MOTION_KEY = "cashmy.reduceMotion";

export function ThemeProvider({ children }) {
  const { user, setUser } = useAuth();
  const [theme, setThemeState] = useState(() => localStorage.getItem(KEY) || "system");
  const [reduceMotion, setReduceState] = useState(() => localStorage.getItem(MOTION_KEY) === "1");
  const systemDark = useMediaQuery("(prefers-color-scheme: dark)");
  const resolved = theme === "system" ? (systemDark ? "dark" : "light") : theme;

  useEffect(() => {
    if (user?.preferences) {
      setThemeState(user.preferences.theme);
      setReduceState(!!user.preferences.reduce_motion);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolved === "dark");
    document.documentElement.classList.toggle("reduce-motion", reduceMotion);
    localStorage.setItem(KEY, theme);
    localStorage.setItem(MOTION_KEY, reduceMotion ? "1" : "0");
  }, [resolved, theme, reduceMotion]);

  const persist = async (patch) => {
    if (!user || user.read_only) return;
    try {
      const { data } = await api.patch("/me/preferences", patch);
      setUser((u) => ({ ...u, preferences: data }));
    } catch {
      // preference stays local for this session
    }
  };
  const setTheme = (t) => { setThemeState(t); persist({ theme: t }); };
  const setReduceMotion = (v) => { setReduceState(v); persist({ reduce_motion: v }); };

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, reduceMotion, setReduceMotion }}>
      <MotionConfig reducedMotion={reduceMotion ? "always" : "user"}>{children}</MotionConfig>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
